import type { BeraterAccessoryMode, BeraterOptionData } from "./types";

const WHITESPACE_CLASS = /[\s\u00a0\u2000-\u200f\u2028-\u202f\u205f-\u206f\u3000\ufeff\u00ad]+/g;
const TOKEN_SEPARATOR_CLASS = /[\s\u00a0\u2000-\u200b\u202f\u205f\u3000_-]+/g;

const SIZE_PAIR_RE = /^(\d{2,3})x(\d{2,3})$/;

export const normalizeSize = (raw: string): string => {
  const normalized = raw
    .toLowerCase()
    .trim()
    .replace(/\(.*?\)/g, "")
    .replace(WHITESPACE_CLASS, "")
    .replace(/\u00d7/g, "x")
    .replace(/cm$/, "");

  const match = normalized.match(SIZE_PAIR_RE);
  if (!match) return normalized;

  const first = Number(match[1]);
  const second = Number(match[2]);
  return `${Math.min(first, second)}x${Math.max(first, second)}`;
};

export const normalizeToken = (raw: string): string =>
  raw
    .toLowerCase()
    .trim()
    .replace(/ß/g, "ss") // Weiß → weiss (option values use ß, flow tokens use ss)
    .replace(TOKEN_SEPARATOR_CLASS, "");

/** Maps a product Farbe option label to a berater color token (wiki § Farbe token cheat sheet). */
export const variantColorToToken = (raw: string): string => {
  const v = normalizeToken(raw);
  if (v.includes("dunkelgrau")) return "dunkelgrau";
  if (v.includes("hellgrau")) return "hellgrau";
  if (v === "grau") return "grau";
  if (v.includes("klarlack")) return "natur";
  if (v.includes("naturweiss")) return "weiss";
  if (v.includes("weisslackiert") || v.includes("weiss")) return "weiss";
  if (v.includes("natur")) return "natur";
  return v;
};

/** True when a flow color token matches a product Farbe option value.
 *  Flow distinguishes `weiss` vs `natur-weiss`; product labels must not collapse via variantColorToToken alone. */
export const colorTokensMatch = (wantRaw: string, variantColorRaw: string): boolean => {
  const want = normalizeToken(wantRaw);
  const variantNorm = normalizeToken(variantColorRaw);
  const mapped = variantColorToToken(variantColorRaw);

  if (want === "naturweiss") return variantNorm === "naturweiss";

  if (want === "weiss") {
    if (variantNorm.includes("natur") && variantNorm.includes("weiss")) return false;
    return mapped === "weiss";
  }

  return want === mapped || want === variantNorm;
};

/** Liquid | json may emit null, "", or a scalar when a list metafield has one value. */
/** CMS single-select values may typo "separate_product" as "sperate_product". */
export const normalizeAccessoryMode = (raw: string): BeraterAccessoryMode => {
  switch (normalizeToken(raw)) {
    case "separateproduct":
    case "sperateproduct":
      return "separate_product";
    case "bundlevariant":
      return "bundle_variant";
    case "addonvariant":
      return "addon_variant";
    case "none":
      return "none";
    default:
      return "none";
  }
};

/** CMS often sets linked_product but leaves accessory_mode empty (→ none). */
export const effectiveAccessoryMode = (option: Pick<BeraterOptionData, "accessoryMode" | "linkedProductHandle" | "value">): BeraterAccessoryMode => {
  const mode = normalizeAccessoryMode(String(option.accessoryMode ?? "none"));
  if (resolveLinkedProductHandle(option) && mode === "none") return "separate_product";
  return mode;
};

/** ponytail: fallback when linked_product missing in CMS — value token on opt-zub-ablage-plano is `ablage`. */
export const resolveLinkedProductHandle = (option: Pick<BeraterOptionData, "value" | "linkedProductHandle">): string | null => {
  if (option.linkedProductHandle) return option.linkedProductHandle;
  const token = normalizeToken(option.value);
  if (token === "ablage" || token === "seitenablage") return "seitenablage-plano";
  return null;
};

type LinkedOption = Pick<BeraterOptionData, "value" | "linkedProductHandle">;

const handleHasSizeWidth = (handle: string | null, width: string): boolean =>
  normalizeToken(handle ?? "").includes(width);

/** CMS repeats `rollrost` with different linked products; pick the handle that encodes the size. */
export const pickOptionForSize = <T extends LinkedOption>(options: T[], token: string, size: string | null): T | undefined => {
  const matches = options.filter((option) => option.value === token);
  if (matches.length <= 1) return matches[0];

  const width = size ? normalizeSize(size).split("x")[0] : "";
  if (!width) return matches[0];

  const sized = matches.filter((option) => handleHasSizeWidth(option.linkedProductHandle, width));
  if (sized.length) return sized[0];

  const generic = matches.filter((option) => !/\d{2,3}/.test(normalizeToken(option.linkedProductHandle ?? "")));
  return generic[0] ?? matches[0];
};

/** Longest first so substring checks stay unambiguous (ponytail: naive includes scan). */
const WOOD_MATERIAL_TOKENS = ["eiche", "kiefer", "birke", "buche"] as const;

export const isWoodMaterialToken = (raw: string): boolean =>
  (WOOD_MATERIAL_TOKENS as readonly string[]).includes(normalizeToken(raw));

export const inferWoodFromText = (...parts: string[]): string | null => {
  for (const part of parts) {
    const token = normalizeToken(part);
    for (const wood of WOOD_MATERIAL_TOKENS) {
      if (token.includes(wood)) return wood;
    }
  }
  return null;
};

/** ponytail: slug/title scan for 90-200-cm / 90x200 when CL option name fails (Gröẞe). */
const SIZE_IN_TEXT_RE = /(\d{2,3})[-x×](\d{2,3})(?:[-\s]|cm|$)/gi;

export const inferSizeFromText = (...parts: string[]): string[] => {
  const sizes = new Set<string>();
  for (const part of parts) {
    for (const match of part.toLowerCase().matchAll(SIZE_IN_TEXT_RE)) {
      sizes.add(normalizeSize(`${match[1]}x${match[2]}`));
    }
  }
  return [...sizes];
};

const AGE_BY_TAG = new Map<string, string>([
  [normalizeToken("Jugendbett"), "jugendliche"],
  [normalizeToken("Kinderbetten"), "kleinkind"],
  [normalizeToken("Kinderbett"), "kleinkind"],
  [normalizeToken("Familienbetten"), "familie"],
  [normalizeToken("Familienbett"), "familie"],
  [normalizeToken("Babybett"), "baby"],
]);

export const inferAgeFromTags = (tags: readonly string[]): string[] => {
  const ages: string[] = [];
  for (const tag of tags) {
    const age = AGE_BY_TAG.get(normalizeToken(tag));
    if (age && !ages.includes(age)) ages.push(age);
  }
  return ages;
};

/** Handle/title beat metafield — separate SKU products encode wood in the slug. */
export const resolveCandidateWood = (handle: string, title: string, metafieldRaw: string | null | undefined): string | null => {
  const inferred = inferWoodFromText(handle, title);
  if (inferred) return inferred;
  if (metafieldRaw) return normalizeToken(String(metafieldRaw));
  return null;
};

/** Longest first — familienbett before bett substrings. */
const BED_TYPE_TOKENS = [
  "familienbett",
  "etagenbett",
  "jugendbett",
  "einzelbett",
  "kinderbett",
  "babybett",
  "panorama",
  "hausbett",
  "bodenbett",
  "doppelbett",
] as const;

export const inferBedTypeFromText = (...parts: string[]): string | null => {
  for (const part of parts) {
    const token = normalizeToken(part);
    for (const bedType of BED_TYPE_TOKENS) {
      if (token.includes(bedType)) return bedType;
    }
  }
  return null;
};

export const resolveCandidateBedType = (handle: string, title: string, metafieldRaw: string | null | undefined): string | null => {
  const inferred = inferBedTypeFromText(handle, title);
  if (inferred) return inferred;
  if (metafieldRaw) return normalizeToken(String(metafieldRaw));
  return null;
};

export const coerceStringArray = (raw: unknown): string[] => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter((value): value is string => typeof value === "string" && value.trim() !== "");
  }
  if (typeof raw === "string" && raw.trim() !== "") return [raw];
  return [];
};

const KNOWN_AGE_TOKENS = new Set(["baby", "kleinkind", "jugendliche", "familie"]);

const expandAgeValues = (raw: unknown): string[] => {
  const tokens: string[] = [];
  for (const value of coerceStringArray(raw)) {
    const normalized = normalizeToken(value);
    if (normalized.startsWith("gid://") || normalized.includes("metaobject")) continue;
    for (const part of value.split(",")) {
      const token = normalizeToken(part);
      if (token === "jungedliche") tokens.push("jugendliche");
      else if (token) tokens.push(token);
    }
  }
  return tokens;
};

export const normalizeAgeTokens = (rawValues: unknown, tags: readonly string[] = []): string[] => {
  const fromMeta = expandAgeValues(rawValues);
  const merged = [...new Set([...fromMeta, ...inferAgeFromTags(tags)])];
  return merged.filter((token) => KNOWN_AGE_TOKENS.has(token));
};

// ponytail: module self-check — fails fast if bed-type inference regresses
if (inferBedTypeFromText("panorama-hausbett-lille-kiefer-weiss") !== "panorama") {
  throw new Error("[ek-berater] normalize self-check failed (panorama before hausbett)");
}

// ponytail: module self-check — Babybett Weiß vs Natur-Weiß must stay distinct
if (!colorTokensMatch("weiss", "Weiß") || colorTokensMatch("weiss", "Natur-Weiß")) {
  throw new Error("[ek-berater] normalize self-check failed (weiss vs natur-weiss)");
}
if (!colorTokensMatch("natur-weiss", "Natur-Weiß") || colorTokensMatch("natur-weiss", "Weiß")) {
  throw new Error("[ek-berater] normalize self-check failed (natur-weiss)");
}
{
  const novaRollrost = [
    { value: "rollrost", linkedProductHandle: "rollrost" },
    { value: "rollrost", linkedProductHandle: "rollrost-nova-140-200" },
  ];
  if (pickOptionForSize(novaRollrost, "rollrost", "90x200")?.linkedProductHandle !== "rollrost") {
    throw new Error("[ek-berater] normalize self-check failed (nova 90 rollrost)");
  }
  if (pickOptionForSize(novaRollrost, "rollrost", "120x200")?.linkedProductHandle !== "rollrost") {
    throw new Error("[ek-berater] normalize self-check failed (nova 120 rollrost)");
  }
  if (pickOptionForSize(novaRollrost, "rollrost", "140x200")?.linkedProductHandle !== "rollrost-nova-140-200") {
    throw new Error("[ek-berater] normalize self-check failed (nova 140 rollrost)");
  }
  if (pickOptionForSize([{ value: "rollrost", linkedProductHandle: "rollrost-plano" }], "rollrost", "120x200")?.linkedProductHandle !== "rollrost-plano") {
    throw new Error("[ek-berater] normalize self-check failed (plano rollrost)");
  }
}
